import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import type { BattleState } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark, remeasureMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** A kiln: a dome of earth, its smoke. */
const KILN = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 13.5C2 9 4.7 6.5 8 6.5s6 2.5 6 7z" fill="currentColor"/><path d="M8 5.5c-1-1.2.6-2-.2-3.6M10.3 5.2c-.7-.9.4-1.5-.2-2.6" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>';
/** A wyvern's wings. */
const WYV = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 9.5 1.5 4.5c.8 3 2.2 4.8 4.3 5.4L8 13l2.2-3.1c2.1-.6 3.5-2.4 4.3-5.4z" fill="currentColor"/></svg>';
/** A flame. */
const FLAME = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5c.6 2.6 3.8 4 3.8 7.6A3.8 3.8 0 0 1 4.2 9.1c0-1.6.8-2.6 1.6-3.3 0 1.4.7 2.2 1.4 2.4C6.6 5.6 7.4 3.6 8 1.5z" fill="currentColor"/></svg>';
/** Hob's cap and an axe. */
const HOB = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 7.5h10M4.2 7.5c0-2.6 1.7-4 3.8-4s3.8 1.4 3.8 4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M5 10.5c.8 2 5.2 2 6 0" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
/** A tree. */
const TREE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5 3 8h2.5L3 12h10l-2.5-4H13z" fill="currentColor"/><path d="M8 12v2.5" stroke="currentColor" stroke-width="1.6"/></svg>';
/** Wind: three strokes. */
const WIND = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 5.5h8.5a2 2 0 1 0-2-2M1.5 8.5h11a2 2 0 1 1-2 2M1.5 11.5h5" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
/** The pine. */
const PINE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1 4 6h2.2L3.5 10h3l-2 3h7l-2-3h3L9.8 6H12z" fill="currentColor"/></svg>';

type Mark = { el: HTMLButtonElement; state: HTMLElement; patch: number; point: THREE.Vector3 };

/**
 * The Slope of the kilns on screen (docs/sendas/druida.md, chapter 2), inside a path's own plate (game/senda-hud.ts)
 * and over the board; a phone's plate kept short:
 *
 * - the kilns (`kilns`): the six in pips (smoking, alight red, cracking pulsing, burst dark, covered white) and their
 *   count, what Hob does, the wyverns (up in the smoke; red while one dives: hit it), the worst kiln (alight: its
 *   seconds; cracking: get away).
 * - Olmo's pine (`strip`): the trees felled in pips, the great fire (its seconds to the strip), the ember coming (its
 *   seconds; red while the hero stands under it), what Hob does.
 * - the counterfire (`counter`, the test): the wind (up, down; red while the smoke bends: its seconds), the pine's
 *   scorches in pips and its heat (above, below), the great fire (bands to the pine, met), the old wyvern (in the smoke,
 *   diving: hit it; breathing: off the line). Over the board, a mark on every patch of grass (tap: light it), gone once
 *   it burns.
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own rules.
 */
export class HornosHud {
  readonly panel: HTMLElement;
  private kind: 'kilns' | 'strip' | 'counter';
  private v = new THREE.Vector3();
  private key = '';
  private rows: Record<string, { row: HTMLElement; text: HTMLElement }> = {};
  private pips: HTMLElement[] = [];
  private marks: Mark[] = [];

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const r = s.rules?.senda?.summer;
    this.kind = r?.kind ?? 'kilns';
    const rows: HTMLElement[] = [];
    const row = (id: string, ico: string, label = ''): HTMLElement => {
      const text = h('span.al-wave');
      const el = h(`div.bh-row.hn-${id}`, {}, h('span.bh-ico', { html: ico }), label ? h('span.bh-label', {}, label) : '', text);
      this.rows[id] = { row: el, text };
      rows.push(el);
      return el;
    };
    if (this.kind === 'kilns') {
      const k = row('kilns', KILN, t('senda.hornos.kilns'));
      this.pips = Array.from({ length: r?.kilns?.at.length ?? 6 }, () => h('i'));
      k.append(h('span.al-pips.hn-pips', {}, ...this.pips));
      row('worst', FLAME);
      row('wyv', WYV);
      row('hob', HOB);
    } else if (this.kind === 'strip') {
      const k = row('trees', TREE, t('senda.hornos.trees'));
      this.pips = Array.from({ length: r?.strip?.trees.length ?? 7 }, () => h('i'));
      k.append(h('span.al-pips.hn-pips', {}, ...this.pips));
      row('front', FLAME);
      row('ember', FLAME);
      row('hob', HOB);
    } else {
      row('wind', WIND);
      const p = row('pine', PINE, t('senda.hornos.pine'));
      this.pips = Array.from({ length: r?.counter?.marks ?? 3 }, () => h('i'));
      p.append(h('span.al-pips.hn-scorch', {}, ...this.pips));
      row('heat', FLAME, t('senda.hornos.heat'));
      row('grand', FLAME);
      row('old', WYV, t('senda.hornos.old'));
      this.buildMarks(s);
    }
    this.panel = h(`div.bh.hn-panel.hn-${this.kind}`, {}, ...rows);
  }

  /** A mark on every patch of grass. */
  private buildMarks(s: BattleState): void {
    const r = s.rules?.senda?.summer?.counter;
    if (!r) return;
    const hint = t('senda.hornos.patchHint');
    r.mid.forEach((m, i) => {
      const state = h('small.al-state.hn-state');
      const el = h(
        'button.al-stone.hn-patch',
        { type: 'button', 'data-test': `fire-patch-${i}`, 'aria-label': hint, title: hint, onpointerup: (e: Event) => (e.stopPropagation(), this.mark(i)) },
        h('span.al-ring'),
        h('span.hn-glyph', { html: FLAME }),
        state,
      ) as HTMLButtonElement;
      this.board.append(el);
      this.marks.push({ el, state, patch: i, point: simToWorld(m.x, m.y, new THREE.Vector3(), 0.5) });
    });
  }

  private set(id: string, text: string, cls?: 'warn' | 'good' | ''): void {
    const r = this.rows[id];
    if (!r) return;
    if (r.text.textContent !== text) r.text.textContent = text;
    r.row.classList.toggle('warn', cls === 'warn');
    r.row.classList.toggle('good', cls === 'good');
  }

  private hide(id: string, off: boolean): void {
    const r = this.rows[id];
    if (r && r.row.hidden !== off) r.row.hidden = off;
  }

  private updateKilns(s: BattleState): void {
    const r = s.rules?.senda?.summer?.kilns;
    const w = s.senda?.summer;
    if (!r || !w) return;
    const covered = w.kiln.filter((v) => v === 4).length;
    this.set('kilns', t('senda.hornos.kilnsState', { n: covered, b: w.bursts }), w.bursts >= 2 ? 'warn' : '');
    this.pips.forEach((p, i) => {
      const v = w.kiln[i] ?? 0;
      p.className = v === 1 ? 'lit' : v === 2 ? 'crack' : v === 3 ? 'burst' : v === 4 ? 'covered' : '';
    });
    // The worst kiln: cracking first, then the one alight soonest to crack.
    let worst = -1;
    for (let k = 0; k < w.kiln.length; k++) {
      const v = w.kiln[k] as number;
      if (v !== 1 && v !== 2) continue;
      if (worst < 0 || (v === 2 && w.kiln[worst] !== 2) || (v === w.kiln[worst] && (w.kt[k] as number) < (w.kt[worst] as number))) worst = k;
    }
    this.hide('worst', worst < 0);
    if (worst >= 0) {
      const v = w.kiln[worst] as number;
      const left = Math.max(1, Math.ceil(((w.kt[worst] as number) + (v === 1 ? r.warn : 0) - s.tick) / TICK_HZ));
      this.set('worst', t(v === 2 ? 'senda.hornos.crack' : 'senda.hornos.lit', { s: left }), 'warn');
    }
    const diving = Object.values(w.wy).some((m) => m[0] === 1);
    this.set('wyv', t(diving ? 'senda.hornos.wyvDive' : 'senda.hornos.wyvAbout'), diving ? 'warn' : '');
    const hob = s.senda?.npcs.find((n) => n.id === 'hob');
    const k = w.hobK;
    const v = k >= 0 ? (w.kiln[k] as number) : 0;
    const hobText = !hob ? '' : k < 0 ? t('senda.hornos.hobDone') : hob.flinch > 0 ? t('senda.hornos.hobHit') : hob.held > 0 ? t('senda.hornos.hobCover') : v === 1 || v === 2 ? t('senda.hornos.hobRun') : t('senda.hornos.hobWalk');
    this.set('hob', hobText, (hob?.flinch ?? 0) > 0 ? 'warn' : k < 0 ? 'good' : '');
  }

  private updateStrip(s: BattleState): void {
    const r = s.rules?.senda?.summer?.strip;
    const w = s.senda?.summer;
    if (!r || !w) return;
    const hob = s.senda?.npcs.find((n) => n.id === 'hob');
    const n = Math.min(r.trees.length, hob?.spot ?? 0);
    this.set('trees', t('senda.hornos.treesState', { n, of: r.trees.length }), n >= r.trees.length ? 'good' : '');
    this.pips.forEach((p, i) => p.classList.toggle('covered', i < n));
    const left = Math.max(0, Math.ceil((w.front - r.line) / Math.max(1, r.speed) / TICK_HZ));
    const hero = s.heroes.find((x) => x.downTicks === 0) ?? s.heroes[0];
    const below = !!hero && hero.y > w.front;
    this.set('front', below ? t('senda.hornos.below') : t(left <= 25 ? 'senda.hornos.frontNear' : 'senda.hornos.front', { s: left }), below || left <= 25 ? 'warn' : '');
    // The ember coming soonest; the hero in a fire.
    let next: { x: number; y: number; at: number; until: number } | null = null;
    let inFire = false;
    for (const e of w.embers) {
      if (s.tick < e.at && (!next || e.at < next.at)) next = e;
      if (hero && s.tick >= e.at && s.tick < e.until && Math.abs(hero.x - e.x) + Math.abs(hero.y - e.y) < r.ember.r * 1.2) inFire = true;
    }
    this.hide('ember', !next && !inFire);
    if (inFire) this.set('ember', t('senda.hornos.burning'), 'warn');
    else if (next) this.set('ember', t('senda.hornos.ember', { s: Math.max(1, Math.ceil((next.at - s.tick) / TICK_HZ)) }), 'warn');
    const sp = hob ? r.spots[hob.spot] : undefined;
    const waiting = !!hob && hob.flinch > 0;
    this.set('hob', !hob || !sp ? t('senda.hornos.hobDone') : waiting ? t('senda.hornos.hobWait') : hob.held > 0 ? t('senda.hornos.hobFell') : t('senda.hornos.hobNext'), waiting ? 'warn' : !sp ? 'good' : '');
  }

  private updateCounter(s: BattleState): void {
    const r = s.rules?.senda?.summer?.counter;
    const w = s.senda?.summer;
    const st = w?.fire;
    if (!r || !w || !st) return;
    const tt = w.t;
    const down = tt >= r.flip;
    const bend = !down && tt >= r.flip - r.warn;
    this.set('wind', down ? t('senda.hornos.windDown') : bend ? t('senda.hornos.windBend', { s: Math.max(1, Math.ceil((r.flip - tt) / TICK_HZ)) }) : t('senda.hornos.windUp'), bend ? 'warn' : '');
    this.set('pine', t('senda.hornos.marks', { n: st.marks, of: r.marks }), st.marks >= r.marks - 1 && st.marks > 0 ? 'warn' : '');
    this.pips.forEach((p, i) => p.classList.toggle('on', i < st.marks));
    const up = r.net.pineUp.some((i) => st.st[i] === 2);
    const dn = r.net.pineDown.some((i) => st.st[i] === 2) && r.net.pineDown.every((i) => st.st[i] !== 0);
    this.set('heat', `${t('senda.hornos.heatUp')}: ${t(up ? 'senda.hornos.yes' : 'senda.hornos.no')} · ${t('senda.hornos.heatDown')}: ${t(dn ? 'senda.hornos.yes' : 'senda.hornos.no')}`, up && dn ? 'good' : '');
    // The great fire: how many bands still between it and the pine.
    let band = 99;
    for (const i of st.front) band = Math.min(band, r.net.band[i] as number);
    const pineBand = (r.net.band[r.net.pineDown[0] as number] as number) - 1;
    const gap = band === 99 ? -1 : band - pineBand - 1;
    this.set('grand', st.meets > 0 ? t('senda.hornos.grandMet') : st.next < 0 ? t('senda.hornos.grandOff') : t('senda.hornos.grand', { n: Math.max(0, gap) }), st.meets > 0 ? 'good' : gap <= 1 && gap >= 0 ? 'warn' : '');
    const m = w.wy[w.boss];
    const oldText = w.bt >= 0 ? t('senda.hornos.oldBreath') : m && m[0] === 1 ? t('senda.hornos.oldDive') : t('senda.hornos.oldAbout');
    this.set('old', oldText, w.bt >= 0 || (m && m[0] === 1) ? 'warn' : '');
    // The marks: on the grass only; the one the hero was sent to, its throw.
    const key = `${st.st.join('')}|${w.job}|${w.done}`;
    if (key !== this.key) {
      this.key = key;
      for (const mk of this.marks) {
        const v = st.st[mk.patch] as number;
        const label = v === 1 ? t('senda.hornos.lit2') : '';
        if (mk.state.textContent !== label) {
          mk.state.textContent = label;
          remeasureMark(mk.el);
        }
        mk.el.classList.toggle('gone', v !== 0 || w.done >= 0);
        mk.el.classList.toggle('near', r.net.pineUp.includes(mk.patch) || r.net.pineDown.includes(mk.patch));
      }
    }
    for (const mk of this.marks) {
      const on = w.job === mk.patch;
      mk.el.classList.toggle('sent', on);
      mk.el.style.setProperty('--k', on ? Math.min(1, w.held / Math.max(1, r.throw)).toFixed(2) : '0');
      mk.el.disabled = s.result !== 'running';
    }
  }

  update(s: BattleState): void {
    if (this.kind === 'kilns') this.updateKilns(s);
    else if (this.kind === 'strip') this.updateStrip(s);
    else this.updateCounter(s);
  }

  /** Every frame: the marks over their places. */
  place(camera: THREE.Camera, w: number, ht: number): void {
    for (const m of this.marks) {
      if (m.el.classList.contains('gone')) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      this.v.copy(m.point).project(camera);
      if (this.v.z > 1 || this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      setStyle(m.el, 'display', '');
      pinMark(m.el, Math.min(w - 30, Math.max(30, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
    }
  }
}
